import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Sparkles, Mail, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';
import BlogCard from '../components/BlogCard';
import { getCategories, getPosts, subscribeNewsletter } from '../services/api';
import config from '../config';

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubscribing, setNewsletterSubscribing] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [categoriesRes, featuredRes, latestRes] = await Promise.all([
        getCategories(),
        getPosts({ featured: true, limit: 1 }),
        getPosts({ limit: 6 })
      ]);

      if (categoriesRes.success) setCategories(categoriesRes.data);
      if (featuredRes.success) setFeaturedPosts(featuredRes.data);
      if (latestRes.success) setLatestPosts(latestRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail.trim()) {
      setNewsletterMessage('Please enter your email address');
      setNewsletterSuccess(false);
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterEmail)) {
      setNewsletterMessage('Please enter a valid email address');
      setNewsletterSuccess(false);
      return;
    }
    
    setNewsletterSubscribing(true);
    setNewsletterMessage('');
    
    try {
      const response = await subscribeNewsletter(newsletterEmail, '', 'home');
      
      if (response.success) {
        setNewsletterMessage(response.message);
        setNewsletterSuccess(true);
        setNewsletterEmail('');
        
        // Clear message after 5 seconds
        setTimeout(() => {
          setNewsletterMessage('');
        }, 5000);
      } else {
        setNewsletterMessage(response.message || 'Subscription failed. Please try again.');
        setNewsletterSuccess(false);
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setNewsletterMessage('Network error. Please try again.');
      setNewsletterSuccess(false);
    } finally {
      setNewsletterSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Home"
        description="Discover quality content on technology, lifestyle, business, travel, and more. Your source for insightful articles and expert opinions. Stay informed with our curated blog posts."
        keywords="home, blog, articles, technology, lifestyle, business, travel, insights, expert opinions, quality content"
        image={`${config.SITE_URL}/og-image.jpg`}
        type="website"
        url={config.SITE_URL}
      />

      <section className="bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-20 animate-fade-in">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full mb-6 animate-slide-down">
              <Sparkles className="text-yellow-500" size={20} />
              <span className="text-sm font-semibold text-gray-700">Welcome to BlogCraft</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 animate-slide-up">
              Discover Stories That <span className="gradient-text">Inspire</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 animate-slide-up">
              Explore quality content on technology, lifestyle, business, and more. 
              Join thousands of readers discovering fresh perspectives daily.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4 animate-slide-up">
              <Link
                to="/blog"
                className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-full transition transform hover:scale-105 shadow-lg"
              >
                Explore Articles
              </Link>
              <Link
                to="/blog"
                className="px-8 py-4 bg-white hover:bg-gray-50 text-gray-900 font-semibold rounded-full transition transform hover:scale-105 shadow-lg flex items-center space-x-2"
              >
                <span>Latest Posts</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {featuredPosts.length > 0 && (
        <section className="container mx-auto px-4 py-16 animate-slide-up">
          <div className="flex items-center space-x-2 mb-8">
            <TrendingUp className="text-yellow-500" size={28} />
            <h2 className="text-3xl font-bold text-gray-900">Featured Post</h2>
          </div>
          <BlogCard post={featuredPosts[0]} featured={true} />
        </section>
      )}

      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Browse by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 text-center hover-lift group-hover:shadow-xl transition-all">
                  <div className="text-4xl mb-3 transform group-hover:scale-110 transition-transform">
                    {category.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition">
                    {category.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{category.post_count} posts</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Latest Articles</h2>
          <Link
            to="/blog"
            className="text-blue-600 hover:text-blue-700 font-semibold flex items-center space-x-2 transition"
          >
            <span>View All</span>
            <ArrowRight size={20} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {latestPosts.map((post, index) => (
            <div
              key={post.id}
              className="animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <BlogCard post={post} />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Stay Updated with Our Newsletter
          </h2>
          <p className="text-blue-100 mb-8 max-w-2xl mx-auto">
            Get the latest articles, insights, and exclusive content delivered straight to your inbox.
          </p>
          
          {newsletterMessage && (
            <div className={`max-w-md mx-auto mb-6 p-4 rounded-lg flex items-center justify-center space-x-2 ${
              newsletterSuccess ? 'bg-green-500/20 text-green-100' : 'bg-red-500/20 text-red-100'
            }`}>
              {newsletterSuccess ? <CheckCircle size={20} /> : <Mail size={20} />}
              <span>{newsletterMessage}</span>
            </div>
          )}
          
          <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto flex flex-col sm:flex-row gap-4">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 px-6 py-4 rounded-full focus:outline-none focus:ring-4 focus:ring-blue-300"
              disabled={newsletterSubscribing}
            />
            <button
              type="submit"
              disabled={newsletterSubscribing}
              className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-full hover:bg-gray-100 transition transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {newsletterSubscribing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-600"></div>
                  <span>Subscribing...</span>
                </>
              ) : (
                <>
                  <Mail size={20} />
                  <span>Subscribe</span>
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default Home;
